import React from 'react';
import { Card, Table } from 'react-bootstrap';
import Icon from './Icon.jsx';
import { Trophy } from 'lucide-react';

// LeagueStandings displays either cumulative standings or head-to-head standings.
// Input contract:
// - props.teams: Array of team objects with shape {
//     _id, name, points|totalPoints, weeklyTotals?: Array<number>
//   }
// - props.resultsByWeek: optional map { weekNumber: { teamId: { points, result } } }
const LeagueStandings = ({ teams = [], resultsByWeek = {}, standings = [], mode = 'cumulative' }) => {
  const rows = standings.length
    ? standings.map((standing) => ({
        id: standing.teamId || standing.id,
        name: standing.teamName || standing.name,
        totalPoints: Number(standing.pointsFor || standing.totalPoints || 0),
        wins: Number(standing.wins || 0),
        losses: Number(standing.losses || 0),
        ties: Number(standing.ties || 0),
        weeksPlayed: Number(standing.weeksPlayed || 0),
        rank: Number(standing.rank || 0),
      }))
    : [...(teams || [])].map((t) => {
        const totalPoints = (t.points ?? t.totalPoints ?? 0);
        const weeksPlayed = Array.isArray(t.weeklyTotals) ? t.weeklyTotals.length : Object.keys(resultsByWeek || {}).length;
        const wins = Object.values(resultsByWeek || {}).reduce((acc, wk) => acc + (wk?.[t._id]?.result === 'W' ? 1 : 0), 0);
        const losses = Object.values(resultsByWeek || {}).reduce((acc, wk) => acc + (wk?.[t._id]?.result === 'L' ? 1 : 0), 0);
        const ties = Object.values(resultsByWeek || {}).reduce((acc, wk) => acc + (wk?.[t._id]?.result === 'T' ? 1 : 0), 0);
        return { id: t._id, name: t.name, totalPoints, wins, losses, ties, weeksPlayed };
      }).sort((a, b) => b.totalPoints - a.totalPoints);

  const isHeadToHead = mode === 'head_to_head';

  return (
    <Card className="modern-card mb-4">
      <div className="modern-card-header">
        <h3 className="modern-card-title"><Icon icon={Trophy} size={18} className="me-2 align-middle" /> League Standings</h3>
        <p className="modern-card-subtitle">
          {isHeadToHead
            ? 'Sorted by wins, then losses, then points scored.'
            : 'Sorted by total points; ties broken by wins.'}
        </p>
      </div>
      <Card.Body>
        <div className="table-responsive">
          <Table hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Team</th>
                <th>{isHeadToHead ? 'Points For' : 'Total Points'}</th>
                <th>Record</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id}>
                  <td>{r.rank || idx + 1}</td>
                  <td><strong>{r.name}</strong></td>
                  <td>{typeof r.totalPoints === 'number' && r.totalPoints.toFixed ? r.totalPoints.toFixed(1) : r.totalPoints}</td>
                  <td>{r.wins}-{r.losses}{r.ties ? `-${r.ties}` : ''}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-secondary">No teams yet</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default LeagueStandings;